import React from 'react';
import { Info } from 'lucide-react';
import { trpc } from '@/utils/trpc-client';
import { Button } from '@/components/forms/ui/button';
import { ChangesetCard } from '@/features/activity/ChangesetCard';
import { NoteCard } from '@/features/activity/NoteCard';
import { CreativeCard } from '@/features/activity/CreativeCard';
import { PlatformSummaryCard } from '@/features/activity/PlatformSummaryCard';
import type { ActivityEntry, ActivityFeedFilters } from 'shared/src/api/activity-feed-api-types';

type ActivityFeedProps = {
  filters?: ActivityFeedFilters;
  sortDirection?: 'asc' | 'desc';
  showCampaignName: boolean;
};

export function ActivityFeed({
  filters,
  sortDirection = 'desc',
  showCampaignName
}: ActivityFeedProps) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.activityFeed.useInfiniteQuery(
      { filters, sortDirection },
      {
        getNextPageParam: lastPage => lastPage.nextCursor
      }
    );

  if (isLoading) {
    return <div className="text-muted-foreground py-8 text-center">Loading activity...</div>;
  }

  const entries = data?.pages.flatMap(p => p.entries) ?? [];
  const hasFilters =
    filters &&
    (filters.activityTypes?.length ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.actorId ||
      filters.actorName ||
      filters.campaignId ||
      filters.accountId ||
      filters.lineItemId);

  if (entries.length === 0) {
    return hasFilters ? <EmptyFilteredState /> : <EmptyState />;
  }

  return (
    <div className="space-y-4">
      <ProvenanceBanner />
      {entries.map((entry, idx) => (
        <ActivityCard
          key={entry.sourceId ?? `${entry.occurredAt}-${idx}`}
          entry={entry}
          showCampaignName={showCampaignName}
        />
      ))}
      {hasNextPage ? (
        <div className="flex justify-center">
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="outline"
            size="sm">
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Private helpers
 * --------------------------------------------------------------------------- */

function ProvenanceBanner() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
      <Info className="mt-0.5 size-4 shrink-0" />
      <span>Bravo changes tracked since October 2024. Earlier activity may be incomplete.</span>
    </div>
  );
}

function EmptyState() {
  return <div className="text-muted-foreground py-12 text-center text-sm">No activity yet.</div>;
}

function EmptyFilteredState() {
  return (
    <div className="py-12 text-center">
      <p className="text-muted-foreground text-sm">No results match the current filters.</p>
    </div>
  );
}

function ActivityCard({
  entry,
  showCampaignName
}: {
  entry: ActivityEntry;
  showCampaignName: boolean;
}) {
  switch (entry.activityType) {
    case 'zoho_note':
      return <NoteCard entry={entry} showCampaignName={showCampaignName} />;
    case 'creative_request':
      return <CreativeCard entry={entry} showCampaignName={showCampaignName} />;
    case 'platform_entity_change':
      return <PlatformSummaryCard entry={entry} showCampaignName={showCampaignName} />;
    default:
      return <ChangesetCard entry={entry} showCampaignName={showCampaignName} />;
  }
}
